import React from "react";
import { Global, Collection, Jobs } from "@sourceflow-uk/sourceflow-sdk";
import { SourceFlowHead } from "@sourceflow-uk/sourceflow-head";
import metaObject from "@/.sourceflow/metadata.json";
import sourceflowEnums from "@/sourceflow.enums";
import SubBanner from "@/components/SubBanner";
import FooterWave from "@/public/site-assets/svg/footer-wave.svg";
import LatestBlogs from "@/components/LatestBlogs";
import WaveIcon1 from "@/public/site-assets/svg/faq-wave-1.svg";
import BlogDetails from "@/components/BlogDetails";
import BlogSchema from "@/components/Shared/BlogPostingSchema";
import he from 'he';

import {
    Container,
} from 'reactstrap';

import { useRouter } from 'next/router';
import { router } from "@/routes";
import { useLocale } from "@/hooks/useLocale";
/**
 *
 * @param global
 * @param meta
 * @returns {JSX.Element}
 * @constructor
 */
export default function IndividualContactUs({
    global,
    meta,
    page,
    blog_category,
    blog_author,
    blog_tags,
    page_blog_category,
    page_blog_tag,
    blogs,
    relatedJobs
}) {
    const locale = useLocale();
    const router = useRouter();


    let breadcrumbTitle;
    if (router.pathname.includes("/events/")) {
        breadcrumbTitle = <a href="/events" target="_self">Events</a>;
    } else if (router.pathname.includes("/downloads/")) {
        breadcrumbTitle = <a href="/downloads" target="_self">Downloads</a>;
    } else if (router.pathname.includes("/reviews/")) {
        breadcrumbTitle = <a href="/reviews" target="_self">Reviews</a>;
    } else if (router.pathname.includes("/recorded-training/")) {
        breadcrumbTitle = <a href="/recorded-training" target="_self">Training</a>;
    } else {
        breadcrumbTitle = <a href="/blog" target="_self">Blog</a>;
    }


    function stripContent(htmlContent) {
        // Remove HTML tags using a regular expression
        let plainText = htmlContent.replace(/<[^>]*>?/gm, '');

        // Decode HTML entities to retain proper characters
        plainText = he.decode(plainText);

        // Remove extra whitespace, including line breaks and multiple spaces
        plainText = plainText.replace(/\s+/g, ' ').trim();

        // Trim to the first 165 characters
        let trimmedText = plainText.substring(0, 165);

        // Add pagination "..." if the text is longer than 165 characters
        if (plainText.length > 165) {
            trimmedText += '...';
        }

        return trimmedText;
    }

    return (
        <>
            <SourceFlowHead
                metaObject={metaObject}
                title={meta.title}
                description={stripContent(page.content)}
                canonical={meta.canonical}
            />
            <div className="bg-light-white">
                <SubBanner
                    global={global}
                    pathPrefix={`${page.url_slug}.blog.page.sub_banner`}
                    heading={`${page.title}`}
                    content={``}
                    breadcrumb_title={
                        <>
                            <span>{breadcrumbTitle}</span>
                            <span className="active">{page.title}</span>
                        </>
                    }
                    wave_color={`light-grey`}
                    blog_style={true}
                    blog_author={blog_author}
                    page_blog_category={page_blog_category}
                    page_blog_tag={page_blog_tag}
                    post={page}
                />
                <BlogDetails
                    global={global}
                    pathPrefix={`${page.url_slug}.blog.page.blog_details`}
                    post={page}
                    blog_category={blog_category}
                    blog_tags={blog_tags}
                    blog_author={blog_author}
                    relatedJobs={relatedJobs}
                    socialShare={false}
                />
                <div className="light-gray-wave">
                    <WaveIcon1 />
                </div>
                <LatestBlogs
                    global={global}
                    pathPrefix={`${page.url_slug}.blog.page.latest_blogs`}
                    post={blogs}
                    heading={`You might also like`}
                    content={``}
                // post_category={page_blog_category}
                />
                <div className="yellow footer">
                    <FooterWave />
                </div>
            </div>
        </>
    );
}

/**
 *
 * @returns {Promise<{props: {meta: {description: string, canonical: string, title: string}, jobs: *, global}}>}
 */
export async function getStaticProps({ params: { url_slug } }) {
    const slug = url_slug.join('/')

    const page = (await Collection("recorded-training")).getJsonItem(
        slug,
        "url_slug"
    );

    const blog_category = (await Collection(`blog_category`)).getItems()
    const blog_tags = (await Collection(`blog_tags`)).getItems()
    const blog_author = (await Collection(`blog_author`))
        .filter(function (post) {
            return (
                post.id === page.blog_author
            );
        })
        .getItems()
    // For single value option only
    // const page_blog_category = (await Collection(`blog_category`))
    //     .filter(function (post) {
    //         return (
    //             post?.id === page.blog_category
    //         );
    //     })
    //     .getItems()
    const page_blog_category = (await Collection(`blog_category`))
        .filter(post => {
            return page?.blog_category.includes(post.id);
        })
        .getItems()

    const page_blog_tag = (await Collection(`blog_tags`))
        .filter(post => {
            return page?.tag.includes(post.id);
        })
        .getItems()

    // Related Jobs
    const sectorElements = page_blog_tag.map((sector) => (
        sector.title
    ));


    let relatedJobs;
    relatedJobs = (await Jobs())
        .getItems()
        .filter(function (job) {
            return (
                job.categories
                    .find((cat) => cat.name === "Job Titles")
                    ?.values?.some((v) => {
                        return sectorElements.some(sector => v.name.includes(sector));
                    })
            );
        })
        .filter((post) =>
            post.url_slug !== page.url_slug
        )
        .filter(function (job) {
            return new Date(job?.expires_at) >= new Date();
        })
        .slice(0, 4);

    const blogs = (await Collection(`blog`)).getItems().slice(0, 6);
    const global = (await Global()).toJson();


    const routerKey = {
        en: router["blog.item"]["en"].replace("[url_slug]", page.url_slug),
    };

    return {
        props: {
            global,
            meta: {
                title: `${page.title} | Tradewind Subs`,
                description: `${sourceflowEnums.seo.genericDescription}`,
                // canonical: sourceflowEnums.seo.baseURL,
                canonical: `${sourceflowEnums.seo.baseURL}${routerKey.en}`,
                // alternate: { link: router[routerKey]["en"], lang: "en" },
            },
            page,
            blog_category,
            blog_author,
            blog_tags,
            page_blog_category,
            page_blog_tag,
            blogs,
            relatedJobs
        },
    };
}


export async function getStaticPaths() {
    // const pathPaths = (await Collection('divisions')).getPaths('url_slug');
    const paths = (await Collection("recorded-training")).getItems().map(i => i.url_slug)
    const splitPaths = paths.map(path => ({ params: { url_slug: path.split('/') } }))

    return {
        paths: splitPaths,
        fallback: false,
    };
}

// -- Reference -- //
// https://github.com/sourceflow-uk/components/pkgs/npm/sourceflow-head
// https://github.com/sourceflow-uk/components/pkgs/npm/sourceflowtext
// https://github.com/sourceflow-uk/components/pkgs/npm/job-search